import React, { useState } from "react";
import { Box, TextField, Typography } from "@mui/material";

import ConfirmDialog from "../../shared/ConfirmDialog";
import usePutGoalOnHold from "hooks/api/goals/usePutGoalOnHold";

export interface PutOnHoldPanelProps {
  /** Controls dialog visibility */
  open: boolean;
  /** Called when the dialog should close */
  onClose: () => void;
  /** Goal name shown in the description */
  goalName: string;
  /** ID of the goal to put on hold */
  goalId: number;
  /** Client ID for query invalidation */
  clientId: string;
}

/**
 * Confirmation dialog for putting a goal on hold.
 * Collects a rationale that is stored with the status change
 * and included in future reassessment treatment plans.
 */
const PutOnHoldPanel: React.FC<PutOnHoldPanelProps> = ({
  open,
  onClose,
  goalName,
  goalId,
  clientId,
}) => {
  const [rationale, setRationale] = useState("");

  const { putGoalOnHold, isPuttingGoalOnHold } = usePutGoalOnHold({
    onSuccess: () => {
      onClose();
      setRationale("");
    },
  });

  const handleClose = () => {
    if (isPuttingGoalOnHold) return;
    setRationale("");
    onClose();
  };

  return (
    <ConfirmDialog
      open={open}
      title="Put Goal On Hold"
      confirmLabel="Put On Hold"
      confirmColor="warning"
      onClose={handleClose}
      onConfirm={() => putGoalOnHold({ goalId, clientId, rationale })}
      confirmDisabled={!rationale.trim()}
      isLoading={isPuttingGoalOnHold}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 5, py: 3 }}>
        <Typography variant="body2">
          &ldquo;{goalName}&rdquo; &mdash; This goal will be paused. You can resume it later.
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography variant="subtitle2">What is your rationale for this action?</Typography>
          <TextField
            multiline
            rows={5}
            fullWidth
            placeholder="Enter your rationale..."
            value={rationale}
            onChange={(e) => setRationale(e.target.value)}
            aria-label="Rationale for putting goal on hold"
          />
          <Typography variant="caption">
            This will be included in future reassessment treatment plans.
          </Typography>
        </Box>
      </Box>
    </ConfirmDialog>
  );
};

export default PutOnHoldPanel;
